/* eslint-disable camelcase */
import React, { FC, useEffect, useState, useMemo, useCallback } from "react"
import SyncableModel from "../../../../database/SyncableModel";
import { Appearance, ViewStyle } from "react-native"
import { CompositeScreenProps } from "@react-navigation/native"
import {
  FarmPrivateShrimpPriceForm as ModelForm,
  ModelCreateScreen,
  Text,
  JalaIcon,
  DeleteModal,
  useRouteName,
} from "../../../../components"
import { observer } from "mobx-react-lite"
import { useStores } from "../../../../models"
import { View } from "native-base";
import { color, nativeBaseTheme } from "../../../../theme";
import JalaAnalytic from "../../../../utils/jala-analytic"
import { ShrimpPrice } from "../../../../database";
import { translate } from "../../../../i18n";
import { navigate } from "../../../../navigators"
import { FarmStoreModel } from "../../../../models/app-store/farm-store/farm-store"

const DELETE_BUTTON: ViewStyle = {
  alignSelf: "flex-end",
  marginVertical: 8,
  marginHorizontal: 16
}

export const FarmPrivateSellingPriceCreateScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function FarmPrivateSellingPriceCreateScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), route: { params = {} } } = props

  // Pull in one of our MST stores
  const {
    appStore: { farmStore, farmId },
    shrimpPriceStore: modelStore,
    farmShrimpPriceStore,
    // userStore: { user }
  } = useStores();

  const { activeBatchStore } = farmStore || FarmStoreModel.create()
  const { routeNameRef, previousRouteNameRef } = useRouteName()

  const [ model, setModel ] = useState<SyncableModel>(null)

  const initialValues = useMemo<any>(() => {
    const data = (model ? model.toArray() : params)

    return {
      region_id: farmStore.farm?.region_id,
      country_id: farmStore.farm?.country_id || 'ID',
      species_id: 1,
      private: true,
      ...data
    }
  }, [farmStore, model, params])

  useEffect(() => {
    if (params?.id) {
      JalaAnalytic.log('Mobile app: visit form private shrimp price - edit');
      const model = ShrimpPrice.hydrate({ ...params });
      setModel(model)
    }
  }, [params, params?.id])

  const onSuccess = useCallback((success: boolean) => {
    if (modelStore.needRefresh) {
      if (success) {
        activeBatchStore.getCycleActualAndPredictions(true, () => {
          modelStore.set('needRefresh', null)
          modelStore.set('pushIdQueue', {})
        })
      } else {
        modelStore.set('needRefresh', null)
        modelStore.set('pushIdQueue', {})
      }
    }
    return success;
  }, [activeBatchStore, modelStore])

  const onSubmit = useCallback(async (data: any) => {
    if (!modelStore) return;
    modelStore.set('loading', true)
    const success = !model
      ? await modelStore.store(data, async (success, model) => {
        await farmShrimpPriceStore.store({ farm_id: farmId, shrimp_price_id: model.getKey() })
      })
      : await modelStore.update(model, data).then(async (success) => {
        await farmShrimpPriceStore.store({ farm_id: farmId, shrimp_price_id: model.getKey() })
        return success;
      })

    JalaAnalytic.log("Mobile app: Form On Submit", {
      form: ModelForm.displayName || 'Unknown',
      data: JSON.stringify(data || null),
      success,
      routeName: routeNameRef.current,
      previousRouteName: previousRouteNameRef.current
    })

    if ((!onSuccess || onSuccess(success)) && success) {
      navigate('Home.Stack', 'Home.Overview.SellingPricePrediction.List')
    }
    modelStore.set('loading', false)
  }, [farmId, farmShrimpPriceStore, model, modelStore, onSuccess, previousRouteNameRef, routeNameRef])

  const handleDelete = useCallback(async () => {
    if (!modelStore) return;
    modelStore.set('loading', true)
    const success = await modelStore.delete(model)

    JalaAnalytic.log("Mobile app: Form Handle Delete", {
      form: ModelForm.displayName || 'Unknown',
      success
    })

    if ((!onSuccess || onSuccess(success)) && success) {
      navigate('Home.Stack', 'Home.Overview.SellingPricePrediction.List')
    }
    modelStore.set('loading', false)
  }, [model, modelStore, onSuccess])

  const renderDeleteButton = useCallback(({ model, colorScheme, loading }) => (
    <View>
      {model && <View flexDirection="row" justifyContent="space-between" alignItems="center" >
        <Text
          tx="farmShrimpPriceForm.delete"
          preset="title4"
          color={color.palette.gray800}
          marginLeft="16px"
        />
        <DeleteModal
          testID="jala:id/delete-button"
          style={DELETE_BUTTON}
          onConfirm={handleDelete}
          deleteModalTitle={translate("farmShrimpPriceForm.deleteModalTitle")}
          deleteModalSubtitle={translate("farmShrimpPriceForm.deleteModalSubtitle")}
          disabled={loading}
        >
          <Text
            tx="delete"
            preset="title4"
            colorScheme={colorScheme}
            color={loading ? nativeBaseTheme.colors.muted["500"] : nativeBaseTheme.colors.danger["500"]}
          />
        </DeleteModal>
      </View>}
      <View flexDirection="row" marginX="16px" marginY="8px">
        <JalaIcon name="info-1" size={15} color={color.palette.gray800} />
        <Text tx="farmShrimpPriceForm.info" preset="body3" color={color.palette.gray700} marginLeft="10px" />
      </View>
    </View>
  ), [handleDelete])

  return (<>
    <ModelCreateScreen
      colorScheme={colorScheme}
      ModelForm={ModelForm}
      initialValues={initialValues}
      modelStore={modelStore}
      model={model}
      onSubmit={onSubmit}
      renderDeleteButton={renderDeleteButton}
    />
  </>)
})
